import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { money } from "@/components/am/shared";

const CATS = [["essential", "Essential pages"], ["optional", "Optional pages"], ["leads", "Lead generation & sales"], ["tracking", "Marketing & tracking"], ["design", "Design & content"], ["technical", "Technical services"]];

function Item({ it, on, busy, onToggle }) {
  const fixed = it.mandatory || it.needed_by.length > 0;
  const tag = it.mandatory ? "Required for publishing & compliance" : it.needed_by.length ? `Needed by ${it.needed_by.join(", ")}` : null;
  const price = it.mandatory ? "Required" : it.quote_required ? "Quote required" : it.price == null ? (it.included ? "In plan" : "Free") : it.included ? `${money(it.price)} · in plan` : `+${money(it.price)}`;
  return (
    <label className={`flex items-start gap-2 rounded-lg border p-2 text-sm ${on ? "border-emerald-500/50 bg-emerald-500/5" : "border-slate-800"} ${fixed ? "cursor-default" : "cursor-pointer"}`} data-testid={`diy-item-${it.key}`}>
      <Checkbox checked={on} disabled={busy || fixed} onCheckedChange={() => onToggle(it.key)} className="mt-0.5" data-testid={`diy-item-toggle-${it.key}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2"><span className="font-medium">{it.label}</span><span className="text-[10px] font-mono text-slate-500">{it.is_page ? "PAGE" : "FEATURE"}</span></div>
        {tag && <div className="text-[11px] text-amber-600 flex items-center gap-1"><Lock className="h-3 w-3" />{tag}</div>}
        {!on && it.requires.length > 0 && <div className="text-[11px] text-slate-500">Also adds: {it.requires.join(", ")}</div>}
      </div>
      <span className="text-xs font-mono shrink-0 text-right" data-testid={`diy-item-price-${it.key}`}>{price}</span>
    </label>
  );
}

export default function DiyItems({ v, setV }) {
  const [busy, setBusy] = useState(false);
  const locked = !!v.quote.locked;
  const [draft, setDraft] = useState(v.items);
  useEffect(() => { setDraft(v.items); }, [v.items]);
  const changed = locked && (draft.length !== v.items.length || draft.some((k) => !v.items.includes(k)));
  const call = async (fn) => {
    setBusy(true);
    try { const r = (await fn()).data; setV(r); if (r.auto_added?.length) toast.info(`Also added because it is needed: ${r.auto_added.join(", ")}`); return r; }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = (key) => {
    const items = draft.includes(key) ? draft.filter((k) => k !== key) : [...draft, key];
    if (locked) setDraft(items); else call(() => api.put(`/diy-sites/${v.project_id}/items`, { items }));
  };
  return (
    <div className="rounded-xl border border-slate-800 p-4 space-y-3" data-testid="diy-items">
      <div className="text-sm font-semibold">Choose your pages and features</div>
      <p className="text-xs text-slate-500">{locked
        ? "Your pages and features were locked with your price when you paid the advance. You can still edit all content and design. To add or remove pages or features, tick them below and request a revised quote; nothing changes until you approve it."
        : "Plans are only a starting point: remove any optional page or feature, even down to a 1-page website. You pay only for what you keep, and the price updates instantly. Items marked Required stay on because publishing and compliance need them."}</p>
      {v.quote.single_page && <p className="text-xs text-emerald-600" data-testid="diy-single-page">1-page website: Contact, Privacy policy and RERA details will appear as sections on your home page.</p>}
      {CATS.map(([c, l]) => {
        const rows = v.catalog.filter((i) => i.category === c);
        return rows.length > 0 && (
          <div key={c}><div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{l}</div>
            <div className="grid md:grid-cols-2 gap-2">{rows.map((it) => <Item key={it.key} it={it} on={draft.includes(it.key)} busy={busy || !!v.change_request} onToggle={toggle} />)}</div></div>
        );
      })}
      {changed && !v.change_request && <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => call(() => api.post(`/diy-sites/${v.project_id}/change-request`, { items: draft }))} data-testid="diy-request-change">Request revised quote</Button>
        <Button variant="secondary" onClick={() => setDraft(v.items)} data-testid="diy-discard-change">Discard</Button></div>}
    </div>
  );
}
